import { Suspense } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { Helmet } from "react-helmet";
import { provide } from "@hilma/tools";
import {
  AuthProvider,
  PrivateRoute,
  PublicOnlyRoute,
  UserProvider,
} from "@hilma/auth";
import { SocketProvider } from "@hilma/socket.io-react";
import {
  I18nProvider,
  useFilesUpload,
  LanguageSwitch,
  GoogleMapsProvider,
  AlertProvider,
  Alert,
  OpeningDatesProvider,
  useDirection,
  socketUri,
  DeliveryOpeningDatesProvider,
} from "@hilma/netina2all";

import {
  Associations,
  CommonQuestions,
  ContactUs,
  Stations,
  Home,
  HowItWorks,
  Profile,
  WhoWeAre,
  Purchase,
  AssociationRegistration,
  StationRegistration,
  PageNotFound,
  Login,
  Prices,
  Unsubscribe,
  LeaveDetails,
} from "./routes";
import {
  SettingsProvider,
  MembersProvider,
  CommonQuestionsProvider,
  PathOptionsProvider,
  BeforeProfileMountedProvider,
  FreeSaleProvider,
  GoogleAnalyticsProvider,
} from "./context";
import {
  BeforePageChangePrompt,
  BeforeProfile,
  Footer,
  FreeSaleRoute,
  Loading,
  Topbar,
} from "./components";
import { useScrollToTop, useBeforeReload, useChangeDirection } from "./hooks";
import { i18n } from "./constants";
import { store } from "./redux";
import "./stylesheets";

const App: React.FC = () => {
  useFilesUpload();
  useScrollToTop();
  useBeforeReload();
  useChangeDirection();
  const direction = useDirection();

  return (
    <div className="App" style={{ direction }}>
      <Helmet>
        <title>מתנות קטנות</title>
      </Helmet>
      <BeforePageChangePrompt />
      <Topbar />
      <Suspense fallback={<Loading />}>
        <LanguageSwitch>
          <Route path="/" exact component={Home} />
          <Route path="/how-it-works" exact component={HowItWorks} />
          <Route path="/distribution-stations" exact component={Stations} />
          <Route
            path="/distribution-stations/registration"
            exact
            component={StationRegistration}
          />
          <Route path="/associations" exact component={Associations} />
          <Route path="/prices" exact component={Prices} />
          <Route
            path="/associations/registration"
            exact
            component={AssociationRegistration}
          />
          <Route path="/who-are-we" exact component={WhoWeAre} />
          <Route path="/common-questions" exact component={CommonQuestions} />
          <Route path="/contact-us" exact component={ContactUs} />
          <PrivateRoute
            path="/profile"
            redirectPath="/login"
            componentName="Profile"
            component={Profile}
          />
          <PublicOnlyRoute
            path="/login"
            redirectPath="/profile"
            exact
            componentName="Login"
            component={Login}
          />
          <Route path="/purchase-success" exact component={BeforeProfile} />
          <Route path="/purchase" exact component={Purchase} />
          <Route path="/free-sale/:link" component={FreeSaleRoute} />
          <Route path="/unsubscribe/:hashedPhone" component={Unsubscribe} />
          {/* Unsubscribe to spam messages */}
          <Route path="/leave-details" component={LeaveDetails} />
          <Route path="/*" component={PageNotFound} />
        </LanguageSwitch>
      </Suspense>
      <Footer />
      <Alert />
    </div>
  );
};

export default provide(
  AlertProvider,
  OpeningDatesProvider,
  DeliveryOpeningDatesProvider,
  SettingsProvider,
  Router,
  [
    AuthProvider,
    {
      logoutOnUnauthorized: true,
      accessTokenCookie: "kloolk",
      twoFactorCookie: "lkool",
    },
  ],
  [UserProvider, { url: "/api/customer/profile" }],
  [SocketProvider, { uri: socketUri, options: { transports: ["websocket"] } }],
  [Provider, { store }],
  PathOptionsProvider,
  FreeSaleProvider,
  [I18nProvider, { i18n }],
  MembersProvider,
  CommonQuestionsProvider,
  GoogleMapsProvider,
  BeforeProfileMountedProvider,
  GoogleAnalyticsProvider
)(App);
